import React, { useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { UserPlusIcon } from '@heroicons/react/24/outline';
import ChatAvatar from './ChatAvatar';
import RoomList from './RoomList';
import SearchInput from './SearchInput';
import type { ChatRoom } from '../../types/chat';
import { useCurrentChatIdentity } from '../../hooks/chat/useCurrentChatIdentity';

interface ContactsRailProps {
  rooms: ChatRoom[];
  activeRoomId?: string | null;
  onSelectRoom: (room: ChatRoom) => void;
  onNewChat: () => void;
  collapsed: boolean;
  onToggleCollapsed: () => void;
  isLoading?: boolean;
}

const ContactsRail: React.FC<ContactsRailProps> = ({
  rooms,
  activeRoomId,
  onSelectRoom,
  onNewChat,
  collapsed,
  onToggleCollapsed,
  isLoading,
}) => {
  const [search, setSearch] = useState('');
  const { fullName, avatarUrl } = useCurrentChatIdentity();

  const filtered = search
    ? rooms.filter((r) => {
        const display = (r.name ?? '') + ' ' + (r.last_message_preview ?? '');
        return display.toLowerCase().includes(search.toLowerCase());
      })
    : rooms;

  return (
    <aside
      className="relative shrink-0 flex flex-col bg-surface-primary transition-[width] duration-200"
      style={{ width: collapsed ? 72 : 320 }}
    >
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
            aria-hidden="true"
            className="brand-gradient absolute inset-0 opacity-50"
          />
      </div>
      <div className="relative flex items-center justify-between px-4 pt-4 pb-3">
        <ChatAvatar src={avatarUrl} name={fullName} size={collapsed ? 32 : 40} />
        {!collapsed && (
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={onToggleCollapsed}
              aria-label="Collapse contacts"
              className="p-1.5 rounded-full text-text-secondary hover:text-text-primary hover:bg-neutral-100 dark:hover:bg-neutral-800"
            >
              <ChevronLeftIcon className="w-5 h-5" />
            </button>
            <button
              type="button"
              onClick={onNewChat}
              aria-label="Start a new chat"
              className="p-1.5 rounded-full text-text-secondary hover:text-primary-500 hover:bg-neutral-100 dark:hover:bg-neutral-800"
            >
              <UserPlusIcon className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>

      {collapsed ? (
        <div className="relative flex flex-col items-center gap-2 pt-1">
          <button
            type="button"
            onClick={onToggleCollapsed}
            aria-label="Expand contacts"
            className="p-1.5 rounded-full text-text-secondary hover:text-text-primary hover:bg-neutral-100 dark:hover:bg-neutral-800"
          >
            <ChevronRightIcon className="w-5 h-5" />
          </button>
          <button
            type="button"
            onClick={onNewChat}
            aria-label="Start a new chat"
            className="p-1.5 rounded-full text-text-secondary hover:text-primary-500 hover:bg-neutral-100 dark:hover:bg-neutral-800"
          >
            <UserPlusIcon className="w-5 h-5" />
          </button>
        </div>
      ) : (
        <>
          <div className="relative px-4 pb-3">
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder="Search contacts…"
            />
          </div>
          <div className="relative flex-1 overflow-y-auto scrollbar-thin">
            <RoomList
              rooms={filtered}
              activeRoomId={activeRoomId}
              onSelect={onSelectRoom}
              isLoading={isLoading}
            />
          </div>
        </>
      )}
    </aside>
  );
};

export default ContactsRail;
